/**
 * Everything this app keeps in localStorage, in one place.
 *
 * Logout previously cleared only auth_token and user_data, leaving roughly ten other keys behind —
 * cached schedules, manager notifications, dismissed banners. On a shared browser those survived
 * a company switch, so the next person to sign in saw fragments of the previous company's data.
 *
 * Listing the keys here means clearing them is a single call that cannot drift out of date the way
 * a hand-written sequence of removeItem calls does.
 */
export const AUTH_KEYS = ['auth_token', 'authToken', 'user_data'] as const;

/**
 * Company-scoped caches. Not credentials, but they are one company's data and must not outlive
 * that company's session.
 */
export const COMPANY_DATA_KEYS = [
  'ai_schedules',
  'manager_notifications',
  'notification_banner_dismissed',
  'last_notification_reset_date',
] as const;

/**
 * Prefixes for keys written with a dynamic suffix (per-shift, per-schedule state). Cleared by
 * prefix so a new one does not need adding here to be cleaned up.
 */
export const COMPANY_DATA_PREFIXES = [
  'shift_',
  'schedule_',
  'notification_',
  'ai_schedule_',
] as const;

/**
 * Deliberately NOT cleared on logout: the kiosk device token identifies the physical device, not
 * the person, and survives sign-out by design. Clearing it would require re-provisioning the
 * kiosk every time someone logs out.
 */
export const DEVICE_KEYS = ['kiosk_device_token'] as const;

export function clearSession(storage: Storage = localStorage): void {
  for (const key of [...AUTH_KEYS, ...COMPANY_DATA_KEYS]) {
    storage.removeItem(key);
  }

  // Snapshot the key list first: removing while enumerating shifts the indices.
  const dynamic: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key && COMPANY_DATA_PREFIXES.some((prefix) => key.startsWith(prefix))) {
      dynamic.push(key);
    }
  }

  for (const key of dynamic) {
    storage.removeItem(key);
  }
}
